import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { X } from 'lucide-react';
import { useIntl } from '../../util/reactIntl';
import { Modal, PrimaryButton, SecondaryButton } from '..';
import PendingReviewOpportunitiesCarousel from '../PendingReviewOpportunitiesCarousel';
import {
  getPendingCoachReviewOpportunitiesApi,
  getPendingCustomerReviewOpportunitiesApi,
} from '../../util/api';
import { PENDING_REVIEW_OPPORTUNITIES_LIMIT } from '../../util/customerReviewOpportunity';
import {
  REVIEWER_ROLE_COACH,
  REVIEWER_ROLE_CUSTOMER,
  completeAutomaticReviewPromptCheck,
  getAutomaticPopupOpportunities,
  recordAutomaticReviewPromptShownForOpportunities,
  shouldAllowAutomaticReviewPromptCheck,
} from '../../util/reviewPromptFrequency';
import css from './PendingCoachReviewModal.module.css';
import reviewPromptButtonCss from '../../styles/reviewPromptButton.module.css';

const LOAD_RETRY_DELAYS_MS = [0, 400, 1200, 2500];

// Root cause of the runaway polling loop (GET .../review-opportunities/pending
// firing on every tab focus, unthrottled, for the lifetime of the page):
// `shouldAllowAutomaticReviewPromptCheck` only becomes "not due" once a prompt
// is actually *shown* (recordAutomaticReviewPromptShownForOpportunities, which
// only runs when opportunities.length > 0). For the very common case of an
// account with zero opportunities ever pending, nothing ever marks it "shown",
// so it stays "due" forever -- and the visibilitychange handler below re-fires
// both endpoints on *every single* tab focus event with no rate limit at all.
// Confirmed live on staging: a single real tab-away/tab-back cycle fires
// several fetch pairs immediately (rapid alt-tabbing, e.g. while using
// DevTools, reproduces "dozens of calls in seconds, never stopping").
// This cooldown makes "checked recently, regardless of result" a real signal,
// independent of the 24h "already shown" cookie.
const AUTO_CHECK_MIN_INTERVAL_MS = 5 * 60 * 1000;

const sleep = ms => new Promise(resolve => window.setTimeout(resolve, ms));

const mergeOpportunities = (...lists) => {
  const merged = new Map();
  lists.flat().forEach(item => {
    const txId = item?.transactionId;
    if (!txId) return;
    const reviewerRole = item?.reviewerRole || REVIEWER_ROLE_CUSTOMER;
    merged.set(`${reviewerRole}:${txId}`, item);
  });
  return [...merged.values()];
};

const addOpenReviewQuery = path => {
  const raw = String(path || '').trim();
  if (!raw) return raw;
  const [pathname, hashPart] = raw.split('#');
  const joiner = pathname.includes('?') ? '&' : '?';
  const withQuery = `${pathname}${joiner}openReview=true`;
  return hashPart ? `${withQuery}#${hashPart}` : withQuery;
};

const PendingCoachReviewModal = ({
  currentUser,
  onManageDisableScrolling = () => {},
}) => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const [opportunities, setOpportunities] = useState([]);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const hasRecordedShowRef = useRef(false);
  // Timestamp of the last time we actually issued the pending-opportunities
  // fetch (mount-time or visibility-triggered), regardless of what it found.
  // See AUTO_CHECK_MIN_INTERVAL_MS above for why this exists.
  const lastAutoCheckAtRef = useRef(0);
  const isTransactionPage = /^\/(sale|order)\//.test(String(location?.pathname || ''));

  const userId = currentUser?.id?.uuid;
  const isAuthenticated = useSelector(state => state.auth?.isAuthenticated);

  const shouldFetchAutomaticPopup = useMemo(() => {
    if (!userId) return false;
    return (
      shouldAllowAutomaticReviewPromptCheck({
        userId,
        reviewerRole: REVIEWER_ROLE_CUSTOMER,
      }) ||
      shouldAllowAutomaticReviewPromptCheck({
        userId,
        reviewerRole: REVIEWER_ROLE_COACH,
      })
    );
  }, [userId]);

  const selectedOpportunity = useMemo(
    () => opportunities.find(o => o.transactionId === selectedTransactionId) || null,
    [opportunities, selectedTransactionId]
  );

  const applyOpportunities = useCallback(
    (list, { recordShow = false } = {}) => {
      const visible = getAutomaticPopupOpportunities(list, { userId });
      setOpportunities(visible);
      setSelectedTransactionId(prev => {
        if (prev && visible.some(o => o.transactionId === prev)) {
          return prev;
        }
        return visible[0]?.transactionId || null;
      });
      const shouldOpen = visible.length > 0;
      setIsOpen(shouldOpen);

      if (shouldOpen && recordShow && userId && !hasRecordedShowRef.current) {
        recordAutomaticReviewPromptShownForOpportunities({ userId, opportunities: visible });
        hasRecordedShowRef.current = true;
      }
    },
    [userId]
  );

  const finishCheck = useCallback(() => {
    if (userId) {
      completeAutomaticReviewPromptCheck({ userId });
    }
    setChecked(true);
  }, [userId]);

  useEffect(() => {
    if (isTransactionPage) {
      setIsOpen(false);
    }
  }, [isTransactionPage]);

  useEffect(() => {
    hasRecordedShowRef.current = false;
    setChecked(false);
    setOpportunities([]);
    setSelectedTransactionId(null);
    setIsOpen(false);
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      finishCheck();
      return undefined;
    }

    if (!shouldFetchAutomaticPopup) {
      finishCheck();
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      lastAutoCheckAtRef.current = Date.now();
      let loaded = [];
      let lastError = null;

      for (let attempt = 0; attempt < LOAD_RETRY_DELAYS_MS.length; attempt += 1) {
        if (cancelled) return;
        if (LOAD_RETRY_DELAYS_MS[attempt] > 0) {
          await sleep(LOAD_RETRY_DELAYS_MS[attempt]);
        }
        if (cancelled) return;
        try {
          const [customerRes, coachRes] = await Promise.allSettled([
            getPendingCustomerReviewOpportunitiesApi({
              limit: PENDING_REVIEW_OPPORTUNITIES_LIMIT,
            }),
            getPendingCoachReviewOpportunitiesApi({
              limit: PENDING_REVIEW_OPPORTUNITIES_LIMIT,
            }),
          ]);
          const customerList =
            customerRes.status === 'fulfilled' ? customerRes.value?.opportunities || [] : [];
          const coachList = coachRes.status === 'fulfilled' ? coachRes.value?.opportunities || [] : [];
          loaded = mergeOpportunities(customerList, coachList);
          if (loaded.length > 0) break;
        } catch (err) {
          lastError = err;
        }
      }

      if (cancelled) return;

      if (loaded.length === 0 && process.env.NODE_ENV === 'development' && lastError) {
        console.warn('[PendingCoachReviewModal] load failed', lastError?.message || lastError);
      }
      applyOpportunities(loaded, { recordShow: true });
      finishCheck();
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [userId, shouldFetchAutomaticPopup, applyOpportunities, finishCheck]);

  useEffect(() => {
    const customerDue = userId
      ? shouldAllowAutomaticReviewPromptCheck({
          userId,
          reviewerRole: REVIEWER_ROLE_CUSTOMER,
        })
      : false;
    const coachDue = userId
      ? shouldAllowAutomaticReviewPromptCheck({
          userId,
          reviewerRole: REVIEWER_ROLE_COACH,
        })
      : false;
    if (!userId || (!customerDue && !coachDue)) {
      return undefined;
    }

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      // Without this guard, every focus/blur cycle (alt-tabbing, switching to
      // DevTools, etc.) re-fires both endpoints for as long as the account
      // has never had a shown opportunity -- i.e. forever, for most accounts.
      if (Date.now() - lastAutoCheckAtRef.current < AUTO_CHECK_MIN_INTERVAL_MS) return;
      const customerDue = shouldAllowAutomaticReviewPromptCheck({
        userId,
        reviewerRole: REVIEWER_ROLE_CUSTOMER,
      });
      const coachDue = shouldAllowAutomaticReviewPromptCheck({
        userId,
        reviewerRole: REVIEWER_ROLE_COACH,
      });
      if (!customerDue && !coachDue) return;

      lastAutoCheckAtRef.current = Date.now();
      Promise.allSettled([
        getPendingCustomerReviewOpportunitiesApi({ limit: PENDING_REVIEW_OPPORTUNITIES_LIMIT }),
        getPendingCoachReviewOpportunitiesApi({ limit: PENDING_REVIEW_OPPORTUNITIES_LIMIT }),
      ])
        .then(([customerRes, coachRes]) => {
          const customerList =
            customerRes.status === 'fulfilled' ? customerRes.value?.opportunities || [] : [];
          const coachList = coachRes.status === 'fulfilled' ? coachRes.value?.opportunities || [] : [];
          const merged = mergeOpportunities(customerList, coachList);
          const visible = getAutomaticPopupOpportunities(merged, { userId });
          if (!visible.length) return;
          applyOpportunities(merged, { recordShow: true });
        })
        .catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [userId, applyOpportunities]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleMaybeLater = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const handleLeaveReview = useCallback(() => {
    if (!selectedOpportunity?.transactionId) return;
    handleClose();
    const defaultPath = `/order/${selectedOpportunity.transactionId}`;
    history.push(addOpenReviewQuery(selectedOpportunity?.actionPath || defaultPath));
  }, [handleClose, history, selectedOpportunity]);

  const introParticipantName =
    opportunities.length === 1
      ? selectedOpportunity?.reviewTarget === 'customer'
        ? selectedOpportunity?.customerDisplayName
        : selectedOpportunity?.coachDisplayName
      : null;
  const isCoachReview = selectedOpportunity?.reviewTarget === 'customer';
  const introTargetLabel = isCoachReview ? 'customer' : 'coach';
  const primaryButtonLabel = isCoachReview ? 'Review Customer' : 'Leave a Review';

  return (
    <Modal
      id="PendingCoachReviewModal"
      isOpen={Boolean(checked && !isTransactionPage && isOpen && opportunities.length > 0)}
      onClose={handleMaybeLater}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      hideDefaultClose
    >
      {opportunities.length > 0 ? (
        <div className={css.root}>
          <div className={css.header}>
            <h2 className={css.title}>Leave a Review</h2>
            <button
              type="button"
              className={css.closeButton}
              onClick={handleMaybeLater}
              aria-label={intl.formatMessage({ id: 'Modal.closeModal' })}
            >
              <X size={22} strokeWidth={2} aria-hidden />
            </button>
          </div>
          <p className={css.body}>
            {introParticipantName ? (
              <>
                How was your session with <strong>{introParticipantName}</strong>? Share your experience
                to help others.
              </>
            ) : (
              <>
                You have sessions waiting for a review. Select one below to review your{' '}
                {introTargetLabel}.
              </>
            )}
          </p>
          <PendingReviewOpportunitiesCarousel
            opportunities={opportunities}
            selectedTransactionId={selectedTransactionId}
            onSelect={setSelectedTransactionId}
          />
          <div className={css.actions}>
            <PrimaryButton
              rootClassName={reviewPromptButtonCss.reviewPromptButton}
              onClick={handleLeaveReview}
              disabled={!selectedTransactionId}
            >
              {primaryButtonLabel}
            </PrimaryButton>
            <SecondaryButton onClick={handleMaybeLater}>Maybe Later</SecondaryButton>
          </div>
        </div>
      ) : null}
    </Modal>
  );
};

export default PendingCoachReviewModal;
